import { useToast } from 'common/hooks/useToast';
import useTranslation from 'common/hooks/useTranslation';
import { useUpdatePhaseMutation } from 'common/slices/submissionApi.slice';
import { useEffect, useState } from 'react';

export const useCandidatePhaseChangeHandler = (
  initialPhase,
  skipTests,
  submissionSlug,
  setInterviewModalOpen = () => {},
) => {
  const toast = useToast();
  const { t } = useTranslation('offers\\details');
  const [currentPhase, setCurrentPhase] = useState(null);
  const [pendingPhase, setPendingPhase] = useState(null);
  const [pendingSubmissionSlug, setPendingSubmissionSlug] = useState(null);
  const [possiblePhases, setPossiblePhases] = useState(null);
  const [updatePhase, { isLoading }] = useUpdatePhaseMutation();

  const PHASES = [
    {
      label: 'candidate-drawer-phase-selector.candidates',
      key: 'all',
      kanbanId: 'candidates',
    },
    {
      label: 'candidate-drawer-phase-selector.evaluation',
      key: 'evaluate',
      kanbanId: 'evaluation',
    },
    {
      label: 'candidate-drawer-phase-selector.classification',
      key: 'rank',
      kanbanId: 'classification',
    },
    {
      label: 'candidate-drawer-phase-selector.interview',
      key: 'interview',
      kanbanId: 'interview',
    },
  ];

  const getPossiblePhases = (phase) => {
    return PHASES.filter((p) => {
      if (p.key === phase) return false;
      if (p.key === 'rank' && skipTests) return false;
      return true;
    }).map((p) => ({ label: p.label, key: p.key }));
  };

  useEffect(() => {
    if (initialPhase) {
      setCurrentPhase(PHASES.find((p) => p.key === initialPhase) || {});
    }
  }, [initialPhase]);

  useEffect(() => {
    const newPossiblePhases = getPossiblePhases(currentPhase?.key || '');
    setPossiblePhases(newPossiblePhases);
  }, [currentPhase]);

  const handlePhaseChange = async (nextPhaseKey) => {
    const nextPhase = PHASES.find((p) => p.key === nextPhaseKey);
    if (!nextPhase || nextPhase.key === currentPhase?.key) return;

    if (nextPhaseKey === 'interview') {
      setPendingPhase(nextPhase);
      setPendingSubmissionSlug(submissionSlug);
      setInterviewModalOpen(true);
    } else {
      confirmPhaseChange(nextPhase);
    }
  };

  const confirmPhaseChange = async (nextPhaseOverride) => {
    const nextPhase = nextPhaseOverride || pendingPhase;
    const submissionId = pendingSubmissionSlug || submissionSlug;
    if (!nextPhase || !submissionId) return;

    try {
      await updatePhase({
        submissionId,
        nextPhase: nextPhase.key,
      }).unwrap();

      toast.newToast(
        'positive',
        t('candidate-drawer-phase-selector.toasts.success', {
          start: t(`kanban.${currentPhase.kanbanId}`),
          end: t(`kanban.${nextPhase.kanbanId}`),
        }),
      );
      setCurrentPhase(nextPhase);
      setPendingPhase(null);
      setPendingSubmissionSlug(null);
      setInterviewModalOpen(false);
    } catch (err) {
      setInterviewModalOpen(false);
      const detail = err?.data?.errors?.[0]?.detail;
      if (err?.status === 400 || err?.status === 403) {
        toast.newToast('negative', detail);
      } else {
        toast.newToast(
          'negative',
          t('candidate-drawer-phase-selector.toasts.something-wrong'),
        );
      }
    }
  };

  return {
    currentPhase,
    handlePhaseChange,
    possiblePhases,
    isLoading,
    confirmPhaseChange,
  };
};
